import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { useFeature } from '../context/AuthContext';

interface ClientRow {
  id_client: number;
  nom_client: string;
  ville: string;
  mode_tma: 'forfait' | 'credit';
  credit_temps_courant: number;
  aff_stats: '0' | '1';
  nb_users: number;
}

export function AdminClientsPage() {
  const canWrite = useFeature('admin_accounts');
  const [items, setItems] = useState<ClientRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    apiGet<{ items: ClientRow[] }>('/api/admin/clients')
      .then((d) => setItems(d.items))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => items.filter((c) => c.nom_client.toLowerCase().includes(q.trim().toLowerCase())),
    [items, q],
  );

  return (
    <div style={{ maxWidth: 900 }}>
      <Link to="/" style={{ fontSize: 'var(--fs-xs)' }}>
        ← retour
      </Link>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 12 }}>Comptes clients</h1>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un client…"
          style={{ flex: 1, padding: 8, border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 'var(--fs-sm)' }}
        />
        {canWrite && (
          <Link to="/admin/clients/new" className="btn primary" style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'nowrap' }}>
            + Nouveau client
          </Link>
        )}
      </div>

      {loading && <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>}
      {err && (
        <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-sm)', padding: 10, background: 'var(--danger-soft)', borderRadius: 6 }}>
          {err}
        </div>
      )}

      {!loading && !err && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              minWidth: 560,
            }}
          >
            <thead style={{ background: 'var(--bg-soft)' }}>
              <tr>
                <th style={th()}>Client</th>
                <th style={th()}>Ville</th>
                <th style={th()}>Mode</th>
                <th style={th('right')}>Crédit (min)</th>
                <th style={th('right')}>Comptes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id_client} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td()}>
                    <Link to={`/admin/clients/${c.id_client}`} style={{ fontWeight: 500 }}>
                      {c.nom_client}
                    </Link>
                  </td>
                  <td style={td()}>{c.ville}</td>
                  <td style={td()}>
                    <span className={`chip ${c.mode_tma === 'credit' ? 'p2' : 'p3'}`}>{c.mode_tma}</span>
                  </td>
                  <td style={td('right')}>{c.mode_tma === 'credit' ? c.credit_temps_courant : '—'}</td>
                  <td style={td('right')}>{c.nb_users}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td style={{ ...td(), color: 'var(--text-mute)' }} colSpan={5}>
                    Aucun client.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function th(align: 'left' | 'right' = 'left'): React.CSSProperties {
  return {
    padding: '8px 10px',
    fontSize: 'var(--fs-xs)',
    textTransform: 'uppercase',
    fontWeight: 700,
    textAlign: align,
    color: 'var(--text-mute)',
  };
}
function td(align: 'left' | 'right' = 'left'): React.CSSProperties {
  return { padding: '8px 10px', textAlign: align };
}
